<template>
    <DialogLayout :class-name="'ai-parse-residence-card-dialog'" @close="handleClose">
        <template #title>{{ t('employee.form.residenceInfo.aiParseResidenceCard') }}</template>
        <template #default>
            <div class="dialog-content">
                <div class="upload-section">
                    <FileUpload
                        :title="t('employee.form.documents.residenceCard.titleFront')"
                        :max-count="maxCount"
                        :max-size="maxFileSizeMb"
                        :tips="t('employee.form.documents.residenceCard.tips')"
                        accept=".jpg,.jpeg,.png,.pdf"
                        :file-list="uploadedFiles"
                        :disabled="false"
                        @update:file-list="handleFileListUpdate"
                        @upload="handleFileUpload"
                        @delete="handleFileDelete"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button
                type="primary"
                :loading="submitting"
                :disabled="isConfirmDisabled"
                :class="{ 'is-disabled-opacity': isConfirmDisabled }"
                @click="handleConfirm"
            >
                {{ t('employee.addCandidateDialog.startParse') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, computed, h } from 'vue'
import { ElMessage } from 'element-plus'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue'
import { uploadAttachmentApi, residenceCardAnalysisApi, type AttachmentInfo } from '@/api/candidate'
import { useI18n } from 'vue-i18n'
import FileUpload from '@/components/business-components/file-upload/index.vue'
import type { AiParseResidenceCardDialogProps, AiParseResidenceCardDialogResult } from './ai-parse-residence-card-dialog-types'
import loadingGif from '@/assets/icons/loading.gif';
interface UploadedFile {
    uid?: string
    name?: string
    fileName?: string
    url?: string
    fileKey?: string
}

const { t } = useI18n()
const props = withDefaults(defineProps<AiParseResidenceCardDialogProps>(), {
    maxFileSizeMb: 10,
    maxCount: 2
})

const emit = defineEmits<{
    cancel: []
    confirm: [data: AiParseResidenceCardDialogResult]
    close: []
}>()

const uploading = ref(false)
const submitting = ref(false)
const uploadedFiles = ref<UploadedFile[]>([])
const uploadedAttachments = ref<AttachmentInfo[]>([])
const loadingModal = ref<ReturnType<typeof ElMessage> | null>(null)

const maxFileSizeMb = computed(() => props.maxFileSizeMb)
const maxCount = computed(() => props.maxCount)

const isConfirmDisabled = computed(() => {
    return uploadedFiles.value.length === 0 || uploading.value
})

const handleFileListUpdate = (fileList: UploadedFile[]) => {
    uploadedFiles.value = fileList
}

const handleFileUpload = async (file: File) => {
    try {
        uploading.value = true
        const result = await uploadAttachmentApi(file)
        uploadedAttachments.value = [
            ...uploadedAttachments.value,
            result
        ]
        uploadedFiles.value = [
            ...uploadedFiles.value,
            {
                uid: result.s3Key || String(Date.now()),
                fileName: result.fileName || file.name,
                url: result.url,
                fileKey: result.s3Key
            }
        ]
        ElMessage.success(t('common.uploadSuccess'))
    } catch (error) {
        console.error('上传在留卡失败:', error)
        ElMessage.error(t('common.uploadFailed'))
    } finally {
        uploading.value = false
    }
}

const handleFileDelete = (file: UploadedFile) => {
    const fileKey = file.fileKey || file.uid || ''
    if (fileKey) {
        uploadedAttachments.value = uploadedAttachments.value.filter(
            (item) => item.s3Key !== fileKey
        )
        uploadedFiles.value = uploadedFiles.value.filter(
            (item) => (item.fileKey || item.uid) !== fileKey
        )
    } else {
        uploadedFiles.value = []
        uploadedAttachments.value = []
    }
}

const handleCancel = () => {
    submitting.value = false
    if (loadingModal.value) {
        loadingModal.value.close()
        loadingModal.value = null
    }
    emit('cancel')
}

const handleConfirm = async () => {
    if (uploadedFiles.value.length === 0) {
        ElMessage.error(t('employee.addCandidateDialog.uploadRequired'))
        return
    }
    submitting.value = true
    loadingModal.value = ElMessage({
        message: t('common.parseLoading'),
        duration: 0,
        showClose: false,
        icon: h('img', { src: loadingGif, style: 'width: 20px; height: 20px;' })
    });
    try {
        const s3key_1 = uploadedAttachments.value[0]?.s3Key
        const s3key_2 = uploadedAttachments.value[1]?.s3Key || ''
        if (!s3key_1) {
            ElMessage.error(t('employee.addCandidateDialog.uploadRequired'))
            return
        }
        const response = await residenceCardAnalysisApi(s3key_1, s3key_2)
        const parsed = (response && typeof response === 'object' && 'data' in response)
            ? (response as { data?: Record<string, unknown> }).data
            : response
        ElMessage.success(t('common.parseSuccess'))
        emit('confirm', {
            attachments: uploadedAttachments.value,
            data: (parsed || undefined) as Record<string, unknown> | undefined
        })
    } catch (error) {
        console.error('在留卡解析失败:', error)
        ElMessage.error(t('common.parseFailed'))
    } finally {
        if (loadingModal.value) {
            loadingModal.value.close()
            loadingModal.value = null
        }
        submitting.value = false
    }
}

const handleClose = () => {
    if (!submitting.value) {
        emit('close')
    }
}
</script>

<style scoped lang="scss">
.ai-parse-residence-card-dialog {
    min-width: 520px;
    background: #ffffff;
}

.dialog-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.upload-section {
    background: #F7F8FA;
    border-radius: 8px;
    padding: 12px 8px;
    gap: 8px;
    display: flex;
    flex-direction: column;
}

.is-disabled-opacity {
    background-color: #8755DE !important;
    opacity: 0.5;
}
</style>
